import React from 'react';
import { Calendar, Clock, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { contactInfo } from '../mock';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_IDX = MONTHS.reduce((acc, m, i) => ({ ...acc, [m]: i, [m.slice(0, 3)]: i }), {});

const ordinal = (d) => {
  if (d % 100 >= 11 && d % 100 <= 13) return 'th';
  return { 1: 'st', 2: 'nd', 3: 'rd' }[d % 10] || 'th';
};

const isPast = (year, month, day) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(year, MONTH_IDX[month], day) < today;
};

const WEEKDAY_2026 = [
  { month: 'August', days: [24] },
  { month: 'September', days: [14] },
  { month: 'October', days: [5, 26] },
  { month: 'November', days: [16] },
  { month: 'December', days: [7] }
];

const WEEKEND_2026 = [
  { month: 'August', day: 15, full: false },
  { month: 'October', day: 3, full: false },
  { month: 'November', day: 21, full: false }
];

const WEEKEND_2027_RANGES = [
  { m1: 'Jan', d1: 9, m2: 'Feb', d2: 28, full: false },
  { m1: 'Feb', d1: 20, m2: 'Apr', d2: 17, full: false },
  { m1: 'Apr', d1: 10, m2: 'Jun', d2: 6, full: false }
];

const HAZMAT_2026 = [
  { month: 'Aug', d1: 17, d2: 18, days: 'Mon-Tue', full: false },
  { month: 'Sep', d1: 5, d2: 6, days: 'Sat-Sun', full: false },
  { month: 'Sep', d1: 28, d2: 29, days: 'Mon-Tue', full: false },
  { month: 'Oct', d1: 10, d2: 11, days: 'Sat-Sun', full: false },
  { month: 'Oct', d1: 12, d2: 13, days: 'Mon-Tue', full: false },
  { month: 'Nov', d1: 2, d2: 3, days: 'Mon-Tue', full: false },
  { month: 'Nov', d1: 28, d2: 29, days: 'Sat-Sun', full: false },
  { month: 'Dec', d1: 5, d2: 6, days: 'Sat-Sun', full: false },
  { month: 'Dec', d1: 14, d2: 15, days: 'Mon-Tue', full: false }
];

const HAZMAT_2027 = [
  { month: 'Jan', d1: 7, d2: 8, days: 'Thu-Fri', full: false },
  { month: 'Jan', d1: 29, d2: 30, days: 'Fri-Sat', full: false }
];

export const Schedule = () => {
  const weekdayClasses = WEEKDAY_2026
    .map((entry) => ({ ...entry, days: entry.days.filter((d) => !isPast(2026, entry.month, d)) }))
    .filter((entry) => entry.days.length > 0)
    .map((entry) => `${entry.month} ${entry.days.map((d) => `${d}${ordinal(d)}`).join(' & ')}`);

  const weekendClasses = [
    ...WEEKEND_2026
      .filter((item) => !isPast(2026, item.month, item.day))
      .map((item) => ({ ...item, date: `${item.month} ${item.day}${ordinal(item.day)}` })),
    ...WEEKEND_2027_RANGES
      .filter((r) => !isPast(2027, r.m2, r.d2))
      .map((r) => ({ ...r, date: `${r.m1} ${r.d1}${ordinal(r.d1)} - ${r.m2} ${r.d2}${ordinal(r.d2)}, 2027` }))
  ];

  const hazmat2026 = HAZMAT_2026.filter((e) => !isPast(2026, e.month, e.d2));
  const hazmat2027 = HAZMAT_2027.filter((e) => !isPast(2027, e.month, e.d2));

  const HazmatDate = ({ entry }) => (
    <span className="font-medium text-white text-xs">
      {entry.month} {entry.d1}<span className="text-[0.7em] align-super">{ordinal(entry.d1)}</span>
      {'-'}
      {entry.d2}<span className="text-[0.7em] align-super">{ordinal(entry.d2)}</span>
    </span>
  );

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="schedule" className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#d97706] text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
            2026 Classes Schedule
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a5f] mb-4">
            Road-Ready Training. Real-World Results.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Precision Training. Professional Drivers. Choose your preferred class schedule and start your journey today!
          </p>
        </div>

        {/* Schedule Tables */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Weekday Classes */}
          <div className="bg-gradient-to-br from-[#f0f9ff] to-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#3b9fd9] rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a5f]">Weekday Classes</h3>
            </div>
            <div className="space-y-3">
              {weekdayClasses.map((date) => (
                <div 
                  key={date}
                  className="py-3 px-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border-l-4 border-[#3b9fd9]"
                >
                  <p className="text-gray-700 font-medium">{date}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Weekend Classes */}
          <div className="bg-gradient-to-br from-[#f0f9ff] to-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#3b9fd9] rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a5f]">Weekend Classes</h3>
            </div>
            <div className="space-y-3">
              {weekendClasses.map((item) => (
                <div 
                  key={item.date}
                  className={`py-3 px-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border-l-4 ${
                    item.full 
                      ? 'bg-gray-100 border-gray-400' 
                      : 'bg-white border-[#3b9fd9]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className={`font-medium ${item.full ? 'text-gray-500' : 'text-gray-700'}`}>
                      {item.date}
                    </p>
                    {item.full && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-500 text-white">
                        CLASS FULL
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* HazMat Classes */}
          <div className="bg-gradient-to-br from-[#d97706] to-[#b45309] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#d97706]" />
              </div>
              <h3 className="text-2xl font-bold text-white">HazMat Classes</h3>
            </div>
            
            {/* 2026 Schedule - Two Columns */}
            {hazmat2026.length > 0 && (
            <div className="mb-3">
              <div className="text-center mb-2">
                <span className="inline-block bg-white/25 text-white text-xs font-bold tracking-wider px-4 py-1 rounded-full uppercase">2026 Schedule</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
                {hazmat2026.map((entry) => (
                  <div 
                    key={`${entry.month}-${entry.d1}`}
                    data-testid={`hazmat-${entry.month} ${entry.d1}`}
                    className="py-1.5 px-2 rounded-lg bg-white/10 backdrop-blur-sm border-l-2 border-white/60 hover:bg-white/20 transition-shadow"
                  >
                    <div className="flex items-center justify-between gap-1">
                      <HazmatDate entry={entry} />
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/20 text-white whitespace-nowrap">
                        {entry.days}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* 2027 Schedule - Two Columns */}
            {hazmat2027.length > 0 && (
            <div className="mb-3">
              <div className="text-center mb-2">
                <span className="inline-block bg-white/25 text-white text-xs font-bold tracking-wider px-4 py-1 rounded-full uppercase">2027 Schedule</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
                {hazmat2027.map((entry) => (
                  <div 
                    key={`${entry.month}-${entry.d1}`}
                    data-testid={`hazmat-${entry.month} ${entry.d1}`}
                    className="py-1.5 px-2 rounded-lg bg-white/10 backdrop-blur-sm border-l-2 border-white/60 hover:bg-white/20 transition-shadow"
                  >
                    <div className="flex items-center justify-between gap-1">
                      <HazmatDate entry={entry} />
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/20 text-white whitespace-nowrap">
                        {entry.days}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
            
            <div className="pt-3 border-t border-white/20">
              <p className="text-white/90 text-sm font-medium text-center mb-3">
                Special certification for hazardous materials transport
              </p>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pro-trucker-learn/artifacts/etthu4ex_Mini-HazMat-Trainer.jpg" 
                  alt="HazMat Training" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6b] rounded-2xl p-8 md:p-12 text-center shadow-xl">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            New Year. New Career.
          </h3>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Sign Up Now! Seats Are Limited.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={() => window.location.href = `tel:${contactInfo.phone}`}
              size="lg" 
              className="bg-[#d97706] hover:bg-[#b45309] text-white text-xl px-10 py-7 shadow-lg hover:shadow-xl transition-all"
            >
              <Phone className="mr-3 w-6 h-6" />
              Call: {contactInfo.phone}
            </Button>
            
            <Button 
              onClick={scrollToContact}
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] text-xl px-10 py-7 transition-all"
            >
              Enroll Now
            </Button>

            <Button 
              onClick={() => window.location.href = 'mailto:diamondtruckingschool@gmail.com'}
              size="lg" 
              data-testid="cta-email-btn"
              className="bg-white/10 hover:bg-white hover:text-[#1e3a5f] text-white border-2 border-[#d97706] text-xl px-10 py-7 transition-all"
            >
              <Mail className="mr-3 w-6 h-6" />
              Email Us
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
